import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Save, KeyRound, Trash2 } from "lucide-react";

export default function EditUserDialog({ open, onClose, user, onSaved }) {
  const [form, setForm] = useState({ name: "", phone: "", specialty: "", room_number: 0, password: "" });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        specialty: user.specialty || "",
        room_number: user.room_number || 0,
        password: "",
      });
    }
  }, [user, open]);

  const save = async () => {
    if (!user) return;
    const body = { name: form.name, phone: form.phone };
    if (user.role === "teacher") {
      body.specialty = form.specialty;
      body.room_number = parseInt(form.room_number || 0, 10);
    }
    if (form.password) body.password = form.password;
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}`, body);
      toast.success("تم حفظ التعديلات");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!user) return;
    if (!window.confirm(`حذف الحساب "${user.name}" نهائياً؟\nهذا الإجراء لا يمكن التراجع عنه.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/users/${user.id}`);
      toast.success("تم حذف الحساب");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(extractError(e)); }
    finally { setDeleting(false); }
  };

  if (!user) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <DialogContent data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black">تعديل حساب — {user.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-1.5">
            <Label className="font-bold">الاسم</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-user-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="font-bold">رقم الهاتف</Label>
            <Input dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-user-phone" />
          </div>
          {user.role === "teacher" && (
            <>
              <div className="space-y-1.5">
                <Label className="font-bold">التخصص</Label>
                <Input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} placeholder="تخاطب، وظيفي، سلوكي..." data-testid="edit-user-specialty" />
              </div>
              <div className="space-y-1.5">
                <Label className="font-bold">رقم القاعة</Label>
                <Select value={String(form.room_number)} onValueChange={(v) => setForm({ ...form, room_number: parseInt(v, 10) })}>
                  <SelectTrigger data-testid="edit-user-room"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">— بدون قاعة —</SelectItem>
                    {[1,2,3,4,5,6].map((n) => (
                      <SelectItem key={n} value={String(n)}>قاعة {n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <div className="space-y-1.5 pt-3 border-t border-slate-100">
            <Label className="font-bold flex items-center gap-1 text-slate-600"><KeyRound className="w-3.5 h-3.5" /> إعادة تعيين كلمة المرور (اختياري)</Label>
            <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="اتركه فارغاً للإبقاء على الحالية" data-testid="edit-user-password" />
            <div className="text-[11px] text-slate-500">تغيير كلمة المرور يُخرج الحساب من جميع الجلسات الحالية.</div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Button onClick={save} disabled={saving || deleting} className="flex-1 h-11 gap-2 bg-sky-600 hover:bg-sky-700" data-testid="edit-user-save-btn">
              <Save className="w-4 h-4" /> {saving ? "جارٍ الحفظ..." : "تعديل"}
            </Button>
            <Button variant="outline" onClick={onClose} disabled={saving || deleting} className="h-11 px-5" data-testid="edit-user-cancel-btn">
              إلغاء
            </Button>
            <Button onClick={remove} disabled={saving || deleting} className="h-11 gap-2 bg-rose-600 hover:bg-rose-700" data-testid="edit-user-delete-btn">
              <Trash2 className="w-4 h-4" /> {deleting ? "جارٍ الحذف..." : "حذف"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
